import type { PrimitiveType } from 'react-intl';

export type Translate = (key: string, values?: Record<string, PrimitiveType>) => string;

export const GENERIC_ERROR_CODE = 'errors.generic.unexpected';

/**
 * Resolves an `error.code` to its translated string, per ADR 0014. A missing
 * code, or a code with no dictionary entry, falls back to the generic
 * unexpected-error string — never a raw key, never the wire `error.message`.
 *
 * `react-intl` returns the key itself when no message is registered for it,
 * which is how the missing-entry case is detected here.
 */
export function translateErrorCode(t: Translate, code?: string, details?: Record<string, unknown>): string {
  if (code === undefined || code === '') {
    return t(GENERIC_ERROR_CODE);
  }

  const resolved = t(code, details as Record<string, PrimitiveType> | undefined);

  return resolved === code ? t(GENERIC_ERROR_CODE) : resolved;
}

/**
 * `translateErrorCode` with a CALLER-supplied fallback. A surface that already
 * has specific copy of its own must not degrade an unregistered code to the
 * generic unexpected-error string: that is strictly less informative than what
 * it showed before. Takes a full dictionary key, exactly as `translateErrorCode`
 * does, so callers keep using one convention (`errors.${code}`).
 */
export function translateErrorCodeOr(
  t: Translate,
  key: string | null,
  fallback: string,
  details?: Record<string, unknown>,
): string {
  if (key === null || key === '') return fallback;

  const resolved = t(key, details as Record<string, PrimitiveType> | undefined);

  return resolved === key ? fallback : resolved;
}
